import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, ClockCounterClockwise } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { NewTestForm } from "@/components/NewTestForm";
import { LiveExecution } from "@/components/LiveExecution";
import { ReportView } from "@/components/ReportView";
import { HistoryList } from "@/components/HistoryList";
import { getRun } from "@/lib/api";

const TERMINAL = ["completed", "failed"];

export const Dashboard = ({ theme, toggleTheme }) => {
  const [view, setView] = useState("new"); // new | history
  const [activeRun, setActiveRun] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const poll = useCallback((id) => {
    stopPoll();
    const tick = async () => {
      try {
        const run = await getRun(id);
        setActiveRun(run);
        if (TERMINAL.includes(run.status)) { stopPoll(); setRefreshKey((k) => k + 1); }
      } catch { /* keep trying */ }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
  }, []);

  useEffect(() => () => stopPoll(), []);

  const onStarted = (id) => { setView("run"); poll(id); };
  const openRun = async (id) => { setView("run"); poll(id); };
  const backToList = () => { stopPoll(); setActiveRun(null); setView("history"); setRefreshKey((k) => k + 1); };
  const goNew = () => { stopPoll(); setActiveRun(null); setView("new"); };
  const goHistory = () => { stopPoll(); setActiveRun(null); setView("history"); };

  const showReport = activeRun && activeRun.status === "completed";

  return (
    <div className="min-h-screen bg-background">
      <Header theme={theme} toggleTheme={toggleTheme} />

      <div className="mx-auto flex max-w-[1500px] gap-6 px-4 py-6 lg:px-8">
        {/* Sidebar */}
        <aside className="sticky top-24 hidden h-fit w-16 shrink-0 flex-col gap-2 sm:flex xl:w-52">
          <NavBtn active={view === "new"} onClick={goNew} icon={Plus} label="New test" testid="nav-new" />
          <NavBtn active={view === "history"} onClick={goHistory} icon={ClockCounterClockwise} label="History" testid="nav-history" />
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          {view === "new" && <NewTestForm onStarted={onStarted} />}
          {view === "history" && <HistoryList onOpen={openRun} refreshKey={refreshKey} />}
          {view === "run" && activeRun && (
            showReport
              ? <ReportView run={activeRun} onBack={backToList} />
              : <LiveExecution run={activeRun} />
          )}
          {view === "run" && !activeRun && (
            <div className="py-20 text-center text-muted-foreground">Loading run…</div>
          )}
        </main>
      </div>
    </div>
  );
};

const NavBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-colors ${
      active ? "bg-primary text-primary-foreground shadow-md shadow-primary/25" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
    <Icon size={20} weight="bold" className="shrink-0" />
    <span className="hidden xl:inline">{label}</span>
  </button>
);
